import React, { memo, useState } from "react";
import { Popper } from "@mui/material";
import {
  MdFilterAlt,
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
} from "react-icons/md";
import { CiFilter } from "react-icons/ci";
import { TEXT_BASE } from "../../../constants/typography";
import TableCheckbox from "./TableCheckbox";
import HeaderFilters from "./HeaderFilters";
import HeaderStats from "./HeaderStats";
import { useTranslation } from "react-i18next";

const HEADER_CELL_CLASS =
  "overflow-hidden whitespace-nowrap text-ellipsis bg-ui-surface-sunken border-r border-r-ui-border-subtle ";

const COLUMN_HEADER_CLASS = `px-2 py-2 text-left font-medium text-ui-text-muted select-none truncate ${TEXT_BASE}`;

const STATIC_HEADER_CLASS = `px-2 py-2 text-left font-medium text-ui-text-muted select-none ${TEXT_BASE}`;

function TableHead({
  expandable,
  selectable,
  showVariants,
  adjustedColumns,
  isSmallDevice,
  selectedRows,
  editableData,
  handleRowSelect,
  DropdownIcon,
  popperId,
  anchorEl,
  filterValues,
  textFieldRef,
  handlePopper,
  setPopperId,
  setFilterValues,
  handleNestedFilter,
  handleGroupToggle,
}) {
  const hasVisibleColumns = adjustedColumns?.some(
    (col) => col?.checked !== false,
  );
  const { t } = useTranslation("common");

  // The heading whose filter pop-up is open (columns with headerFilters).
  const [openFilters, setOpenFilters] = useState(null);

  const applyHeaderFilter = (fieldName, value) => {
    setFilterValues((prev) => ({ ...prev, [fieldName]: value }));
    handleNestedFilter(value, fieldName);
  };

  const getStickyHeaderClass = (column) => {
    if (isSmallDevice) return "";

    if (column?.fixed === "left") return "sticky left-0 z-20";
    if (column?.fixed === "right") return "sticky right-0 z-20";

    return "";
  };

  const groupChildCounts = {};

  adjustedColumns?.forEach((column) => {
    if (column?.checked === false) return;
    if (column?.groupParentId === undefined) return;

    groupChildCounts[column.groupParentId] =
      (groupChildCounts[column.groupParentId] ?? 0) + 1;
  });

  return (
    <thead className="sticky top-0 z-30 bg-ui-surface-sunken">
      <tr>
        {expandable && (
          <th className={`${HEADER_CELL_CLASS} ${STATIC_HEADER_CLASS}`}>
            <div className="flex items-center justify-center">
              <img
                src={DropdownIcon}
                alt=""
                className="invisible pointer-events-none"
              />
            </div>
          </th>
        )}

        {selectable && hasVisibleColumns && (
          <th className={`${HEADER_CELL_CLASS} ${STATIC_HEADER_CLASS}`}>
            <div className="flex items-center justify-center">
              <TableCheckbox
                checked={
                  editableData?.length > 0 &&
                  selectedRows?.length === editableData?.length
                }
                indeterminate={
                  selectedRows?.length > 0 &&
                  selectedRows?.length < editableData?.length
                }
                onChange={() => handleRowSelect("all")}
              />
            </div>
          </th>
        )}

        {showVariants && (
          <th className={`${HEADER_CELL_CLASS} ${STATIC_HEADER_CLASS}`}>
            {t("table.variant")}
          </th>
        )}

        {adjustedColumns?.map((column, index) => {
          if (column?.checked === false || column?.collapsed) return null;

          // A column with headerFilters opens the filter pop-up instead of
          // the single header search; every other column is unchanged.
          const hasHeaderFilters =
            Array.isArray(column?.headerFilters) &&
            column.headerFilters.length > 0;
          const isSearchable = !hasHeaderFilters && column.searchable;

          const filterFields = hasHeaderFilters
            ? column.headerFilters.map((filter) => filter?.field_name)
            : [column?.field_name];
          const hasActiveFilter = filterFields.some(
            (fieldName) =>
              filterValues[fieldName] !== undefined &&
              filterValues[fieldName] !== null &&
              String(filterValues[fieldName]).trim() !== "",
          );

          const childCount = groupChildCounts[column.id] ?? 0;
          const isGroupParent = column?.isGroupParent && childCount > 0;
          const groupCollapsed = column?.groupCollapsed;
          const groupName = column.group?.label ?? column.label;

          return (
            <th
              key={column.field_name || index}
              style={{
                maxWidth: column.width,
                backgroundColor: column.headerColor,
                color: column.headerTextColor,
              }}
              className={`${HEADER_CELL_CLASS} ${COLUMN_HEADER_CLASS} ${getStickyHeaderClass(
                column,
              )} ${isSearchable || hasHeaderFilters ? "cursor-pointer" : ""} ${
                hasActiveFilter
                  ? "!bg-brand-light !border-b-state-info-border"
                  : ""
              }`}
            >
              <div
                className="relative flex w-full items-center justify-between gap-1"
                onClick={(event) => {
                  if (hasHeaderFilters) {
                    const anchor = event.currentTarget;
                    setOpenFilters((open) =>
                      open?.id === column.id ? null : { id: column.id, anchor },
                    );
                  } else if (isSearchable) {
                    handlePopper(event, column.field_name);
                  }
                }}
              >
                {/* Columns with headerStats show their numbers under the
                    label (on phones they're chips above the table). */}
                {column?.headerStats?.length && !isSmallDevice ? (
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{column.label}</span>
                    <HeaderStats
                      stats={column.headerStats}
                      filterValues={filterValues}
                      onApply={applyHeaderFilter}
                    />
                  </span>
                ) : (
                  <span className="truncate">{column.label}</span>
                )}

                <div className="flex shrink-0 items-center gap-1">
                  {(isSearchable || hasHeaderFilters) &&
                    (hasActiveFilter ? (
                      <MdFilterAlt className="shrink-0 text-brand" />
                    ) : (
                      <CiFilter className="shrink-0" />
                    ))}

                  {isGroupParent && (
                    <button
                      type="button"
                      aria-expanded={!groupCollapsed}
                      title={
                        groupCollapsed
                          ? t("table.showGroup", {
                              count: childCount,
                              group: groupName,
                            })
                          : t("table.hideGroup", {
                              count: childCount,
                              group: groupName,
                            })
                      }
                      onClick={(event) => {
                        event.stopPropagation();
                        handleGroupToggle?.(column.id);
                      }}
                      className={`flex h-[18px] shrink-0 items-center gap-0.5 rounded px-1 text-[10px] font-bold leading-none transition-colors ${
                        groupCollapsed
                          ? "bg-brand text-brand-contrast hover:bg-brand-hover"
                          : "text-ui-text-subtle hover:bg-ui-border hover:text-brand"
                      }`}
                    >
                      {groupCollapsed ? (
                        <>
                          {childCount}
                          <MdKeyboardDoubleArrowRight size={12} />
                        </>
                      ) : (
                        <MdKeyboardDoubleArrowLeft size={14} />
                      )}
                    </button>
                  )}
                </div>
              </div>

              {hasHeaderFilters && openFilters?.id === column.id && (
                <HeaderFilters
                  label={column.label}
                  filters={column.headerFilters}
                  filterValues={filterValues}
                  anchorEl={openFilters.anchor}
                  onChange={applyHeaderFilter}
                  onClose={() => setOpenFilters(null)}
                />
              )}

              {isSearchable && (
                <Popper
                  id={popperId}
                  open={popperId === column.field_name}
                  anchorEl={anchorEl}
                  placement="top"
                  className="z-[60] animate-popper-in"
                  modifiers={[
                    {
                      name: "offset",
                      options: { offset: [0, 6] },
                    },
                    {
                      name: "preventOverflow",
                      options: { boundariesElement: "viewport" },
                    },
                  ]}
                >
                  <div className="relative w-[160px] max-w-full rounded-md bg-ui-surface-raised p-1 shadow-ui-md">
                    <button
                      type="button"
                      onClick={() => {
                        setPopperId(null);

                        setFilterValues((prev) => ({
                          ...prev,
                          [column.field_name]: "",
                        }));

                        handleNestedFilter("", column.field_name);
                      }}
                      className="absolute -top-2 -right-2 rounded-full bg-ui-text-subtle p-[4px] text-ui-surface shadow-2xl transition-all duration-150 hover:bg-ui-text-muted"
                      title={t("actions.close")}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-[10px] w-[10px]"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 8.586l4.95-4.95 1.414 1.414L11.414 10l4.95 4.95-1.414 1.414L10 11.414l-4.95 4.95-1.414-1.414L8.586 10 3.636 5.05l1.414-1.414L10 8.586z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>

                    <input
                      id={column.field_name}
                      type="text"
                      placeholder={t("table.searchIn", { name: column.label })}
                      value={filterValues[column.field_name] || ""}
                      ref={textFieldRef}
                      onChange={(event) => {
                        const newValue = event.target.value;

                        setFilterValues((prev) => ({
                          ...prev,
                          [column.field_name]: newValue,
                        }));

                        handleNestedFilter(newValue, column.field_name);
                      }}
                      className={`w-full rounded-sm px-2 py-[5px] ${TEXT_BASE} text-ui-text placeholder-ui-text-subtle shadow-inner outline-none ring-0 transition-all duration-150 focus:outline-none focus:ring-0 active:outline-none active:ring-0 visited:outline-none visited:ring-0`}
                    />
                  </div>
                </Popper>
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

export default memo(TableHead);
